<template>
  <main class="ma-4">
    <!-- title -->
    <simple-date-header :date="translations.pileDetail + plantName" :goTo="previous"></simple-date-header>

    <!-- deactivated pile: read only -->
    <v-alert v-if="isInactivePile" type="info" outlined dense role="status">{{ translations.deactivatedPileReadOnly }}</v-alert>

    <!-- pile's data -->
    <div style="background-color:white">
      <v-row class="ma-0 justify-space-between" align="center">
        <v-col cols="2" align="center">
          <div v-if="pile">
            <!-- pie chart if pile type is limited -->
            <PieChart v-if="pileTypeIsLimited" :pile="pile" :pile-type="pileType" />

            <!-- pile's icon (STANDARD type) -->
            <v-icon v-else size="150">$pile</v-icon>
          </div>
        </v-col>

        <v-col cols="9">
          <!-- pile's info -->
          <v-row class="pa-2" v-if="pile">
            <v-col cols="8">
              <v-row>
                <v-col cols="6">
                  <span class="mr-4 pile-info">{{ translations.letter }}</span>{{ pile.letter }}
                </v-col>
                <v-col cols="6">
                  <span class="mr-6 pile-info">{{ translations.typology }}</span>{{ pile.type }}
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6">
                  <span class="mr-4 pile-info">{{ translations.description }}</span>{{ pile.description }}
                </v-col>
                <v-col cols="6">
                  <span class="mr-4 pile-info">{{ translations.manufacturer }}</span>{{ pileTypeIsLimited || ['SPECIALv2'].includes(pile.type) ? '-' : pile.manufacturer.name }}
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="4">
              <!-- local unit / project -->
              <v-row>
                <v-col cols="6">
                  <span class="mr-4 pile-info" :class="{ 'el-no-wrap': pile.type === 'SPECIALv2' }" :style="pile.pileLocalUnitProjects.length > 1 && pile.type === 'SPECIAL' ? 'display:block' : ''">{{ ['SPECIAL', 'SPECIALv2'].includes(pile.type) ? translations.localUnitProject : translations.localUnit }}<v-icon v-if="pile.type === 'SPECIALv2'" class="ml-2" @click="openCertificateDialog()">{{ icons.mdiOpenInNew }}</v-icon></span>
                  <span v-if="pile.type !== 'SPECIALv2'">{{ pileLocalUnitProjectName }}</span>
                </v-col>
              </v-row>

              <!-- year -->
              <v-row>
                <v-col cols="6">
                  <span class="mr-4 pile-info">{{ translations.year }}</span>{{ pile.year }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- pile's counters -->
          <v-row v-if="pile">
            <v-col cols="4">
              <v-avatar color="main" size="46">
                <v-icon fab color="white" large>{{ icons.mdiEqual }}</v-icon>
              </v-avatar>
              <span class="ml-2" style="font-size:1.2em">{{ `${toFloat(pile.totalTons)}  ${translations.tons}` }}</span>
              <p>{{ translations.remainder.toLowerCase() }}</p>
            </v-col>
            <v-col cols="4">
              <v-avatar color="#ffcc00" size="46">
                <v-icon fab color="white" large>{{ icons.mdiArrowDown }}</v-icon>
              </v-avatar>
              <span class="ml-2" style="font-size:1.2em">{{ `${toFloat(pile.loadedTons)}  ${translations.tons}` }}</span>
              <p>{{ translations.load.toLowerCase() }}</p>
            </v-col>
            <v-col cols="4">
              <v-avatar color="green" size="46">
                <v-icon fab color="white" large>{{ icons.mdiArrowUp }}</v-icon>
              </v-avatar>
              <span class="ml-2" style="font-size:1.2em">{{ toFloat(pile.unloadedTons) + ' ' + translations.tons }}</span>
              <p>{{ translations.unload.toLowerCase() }}</p>
            </v-col>
          </v-row>
        </v-col>

        <!-- pile's certificate and report-->
        <v-col v-if="pile" cols="1">
          <v-row class="pa-3 mt-1 mb-4" v-if="pile.type !== 'SPECIALv2'">
            <div class="d-flex flex-column align-center">
              <!-- direct download of single certificate -->
              <v-btn v-if="pile.pileLocalUnitProjects.length === 1" icon :disabled="disableCertificate" :href="certificateFile" :download="certificateName">
                <v-icon x-large color="red">{{ icons.mdiFilePdfOutline }}</v-icon>
              </v-btn>

              <!-- open pop-up with list of certificates-->
              <v-btn v-else icon :disabled="disableCertificate" @click="openCertificateDialog()">
                <v-icon x-large color="red">{{ icons.mdiFilePdfOutline }}</v-icon>
              </v-btn>
              <p style="font-size:0.8em">{{ translations.download }}</p>
            </div>
          </v-row>
          <v-row class="pa-3">
            <div class="d-flex flex-column align-center">
              <v-btn icon @click="pileMovementReportWrap()">
                <v-icon x-large color="main">{{ icons.mdiPrinter }}</v-icon>
              </v-btn>
              <p style="font-size:0.8em">{{ translations.print }}</p>
            </div>
          </v-row>
        </v-col>
      </v-row>

      <!-- movements table -->
      <smart-table v-if="pile" ref="smartTable" class="pile-movements-table"
                   :headers="headers()"
                   :search-function="searchFunction"
                   preference-key="PileMovements">

        <!-- smart table slot results -->
        <template v-slot:results="{ item: item }">
          <!-- id (printed on the DDT, it never changes) -->
          <td>
            <span>{{ item.id }}</span>
          </td>

          <!-- type -->
          <td>
            <span class="el-no-wrap">{{ item.type === 'LOAD' ? translations.load : translations.unload }}</span>
          </td>

          <!-- registryId -->
          <td>
            <div class="el-no-wrap">{{ item.registryId }}</div>
          </td>

          <!-- date -->
          <td>
            <span>{{ toHumanDate(item.date) }}</span>
          </td>

          <!-- manufacturer -->
          <td>
            <span>{{ item.manufacturer ? item.manufacturer.name : '-' }}</span>
          </td>

          <!-- local unit/ project -->
          <td>
            <span>{{ item.localUnit ? item.localUnit.name : item.project ? item.project.keyCode : '-' }}</span>
          </td>

          <!-- part -->
          <td v-if="isSpecial">
            <span>{{ item.part ? item.part.keyCode : '-' }}</span>
          </td>

          <!-- certificate -->
          <td v-if="pile.type === 'SPECIALv2'">
            <div v-if="item.project || item.localUnit" class="d-flex align-center">
              <v-checkbox :disabled="isCertified(item) || canReadOnly" :ripple="false" dense hide-details class="ma-0 pa-0" :input-value="hasDocument(item)" color="main" @change="checkboxUpdated($event, item)"></v-checkbox>
              <v-icon :disabled="canReadOnly" size="20" @click="handleFileUpload(item)">{{ icons.mdiUpload }}</v-icon>
            </div>
          </td>

          <!-- carrier -->
          <td>
            <span>{{ item.carrier ? item.carrier.name : '-' }}</span>
          </td>

          <!-- load -->
          <td>
            <span>{{ toFloat(item.load) }}</span>
          </td>

          <!-- unload -->
          <td>
            <span>{{ item.type === 'LOAD' ? '-' : toFloat(item.unload) }}</span>
          </td>

          <!-- remainder -->
          <td>
            <span>{{ item.type === 'LOAD' ? toFloat(item.remainder) + ' (' + toFloat(item.unload) + ')' : toFloat(item.remainder) }}</span>
          </td>

          <!-- destination -->
          <td v-if="isSpecial">
            <span>{{ item.destination }}</span>
          </td>

          <!-- unload operations -->
          <td>
            <ul class="ma-0 pa-0 el-no-wrap">
              <li v-for="(operation, i) in [item.movedFrom].concat(item.unloadOperations)" v-bind:key="i" style="list-style: none">{{ operation }}</li>
            </ul>
          </td>

          <template v-if="isSpecial && ddt">
            <!-- m3 -->
            <td>
              <span>{{ item.ddt.m3 ? item.ddt.m3 : '-' }}</span>
            </td>

            <!-- additional information -->
            <td>
              <span>{{ item.ddt.additionalInfo ? item.ddt.additionalInfo : '-' }}</span>
            </td>

            <!-- ddt registryId -->
            <td>
              <div class="el-no-wrap">
                <span>{{ item.ddt.registryId ? item.ddt.registryId : '-' }}</span>
                &nbsp;
                <v-icon v-if="item.ddt.registryId" small @click="ddtReport({ ddtId: item.ddt.id })">
                  {{ icons.mdiDownload }}
                </v-icon>
              </div>
            </td>
          </template>

          <!-- attachment: belongs to this movement only -->
          <td>
            <!-- file icon: the file name and its commands open in a dropdown -->
            <v-menu v-if="item.attachmentFilename" offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small :title="item.attachmentFilename" :aria-label="item.attachmentFilename" v-bind="attrs" v-on="on">
                  <v-icon color="main">{{ icons.mdiFileOutline }}</v-icon>
                </v-btn>
              </template>
              <v-list dense class="pt-0 attachment-menu">
                <div class="attachment-menu-title grey lighten-4 main--text font-weight-bold text-right">{{ item.attachmentFilename }}</div>
                <v-divider class="mb-1"></v-divider>
                <!-- actions on the right: label, then icon -->
                <v-list-item @click="downloadAttachment(item)">
                  <v-list-item-title class="text-right">{{ translations.attachmentDownload }}</v-list-item-title>
                  <v-list-item-icon class="ml-3"><v-icon size="20">{{ icons.mdiDownload }}</v-icon></v-list-item-icon>
                </v-list-item>
                <v-list-item v-if="!canReadOnly" @click="handleAttachmentUpload(item)">
                  <v-list-item-title class="text-right">{{ translations.attachmentReplace }}</v-list-item-title>
                  <v-list-item-icon class="ml-3"><v-icon size="20">{{ icons.mdiUpload }}</v-icon></v-list-item-icon>
                </v-list-item>
                <v-list-item v-if="!canReadOnly" @click="deleteAttachmentConfirmation(item)">
                  <v-list-item-title class="text-right red--text">{{ translations.attachmentDelete }}</v-list-item-title>
                  <v-list-item-icon class="ml-3"><v-icon size="20" color="red">{{ icons.mdiDelete }}</v-icon></v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- no file: upload only -->
            <v-icon v-else-if="!canReadOnly" size="20" :title="translations.attachmentUpload" @click="handleAttachmentUpload(item)">{{ icons.mdiUpload }}</v-icon>
            <span v-else>-</span>
          </td>

          <!-- table actions -->
          <td>
            <div class="el-no-wrap" v-if="!canReadOnly">
              <v-icon small class="mr-1" @click="startEditMode(item)">{{ icons.mdiSquareEditOutline }}</v-icon>
              <v-icon small class="mr-1" color="red" :disabled="!!deletion" :aria-label="translations.delete + ' ' + item.id" @click="requestDeletion(item)">{{ icons.mdiDelete }}</v-icon>
            </div>
          </td>
        </template>

        <!-- smart table slot FOOTER-ITEM-1: new load/unload -->
        <template v-slot:FOOTER-ITEM-1>
          <v-btn fab x-small color="#ffcc00" class="white--text ml-4 mr-4" :disabled="pileTypeIsLimited && isClosed" @click="saveLoad()" :style="{visibility: canReadOnly ? 'hidden' : 'visible'}">
            <v-icon color="white">{{ icons.mdiPlus }}</v-icon>
          </v-btn>
          <v-btn fab x-small color="green" class="white--text" :disabled="(pile.type === 'SPECIALv2' ? !hasDocumentOrCertificate : disableCertificate) || isEmpty || (pileTypeIsLimited && !isClosed)" @click="saveUnload()" :style="{visibility: canReadOnly ? 'hidden' : 'visible'}">
            <v-icon color="white">{{ icons.mdiPlus }}</v-icon>
          </v-btn>
        </template>
      </smart-table>

      <div v-if="deletion && deletion.phase === 'checking'" class="d-flex align-center ma-3">
        <v-progress-circular indeterminate color="main" size="20" class="mr-2" :aria-label="translations.loading" />
        <span role="status">{{ translations.checkingLinkedUnloads }}</span>
        <v-btn small text color="main" class="ml-2" @click="cancelDeletion()">{{ translations.close }}</v-btn>
      </div>

      <!-- error message -->
      <v-snackbar content-class="text-center" v-model="showErrorAlert" absolute bottom color="error" outlined text timeout="4000">{{ errorMessage }}</v-snackbar>

      <!-- success message -->
      <v-snackbar content-class="text-center" v-model="showSuccessAlert" absolute bottom color="success" outlined text timeout="4000">{{ successMessage }}</v-snackbar>

      <!-- attachment file picker: any file type, opened by the row's upload icon -->
      <input ref="attachmentUploader" class="d-none" type="file" @change="onAttachmentFileChanged" />

      <!-- certificate file picker (pdf), opened by the row's certificate upload icon or the certificates dialog -->
      <input ref="uploader" class="d-none" type="file" accept="application/pdf" @change="onFileChanged" />

      <v-dialog :value="showDeletionDialog" max-width="1000px" scrollable
                :persistent="!!deletion && deletion.phase === 'deleting'" @input="!$event && cancelDeletion()">
        <v-card v-if="deletion" class="load-deletion-preview">
          <v-card-title class="justify-center">
            <h3 class="main--text">{{ translations.deleteLoadTitle }}</h3>
          </v-card-title>
          <v-card-text>
            <v-progress-linear v-if="deletion.phase === 'deleting'" indeterminate color="main" :aria-label="translations.loading" />
            <v-alert v-if="deletion.error" type="error" outlined role="alert">{{ deletion.error }}</v-alert>
            <template v-if="deletion.preview">
              <p>{{ translations.loadDeletionEffects }}</p>
              <section v-for="section in deletionSections" :key="section.title" class="mb-5">
                <h4 class="mb-2">{{ section.title }}</h4>
                <v-simple-table dense class="deletion-movements">
                  <thead>
                    <tr>
                      <th>{{ translations.id }} / {{ translations.registryId }}</th>
                      <th>{{ translations.date }}</th>
                      <th>{{ translations.piles }} / {{ translations.category }}</th>
                      <th>{{ section.selected ? translations.load + ' ' + translations.tons : translations.totalUnloadTons }}</th>
                      <th>{{ section.selected ? translations.remainder + ' ' + translations.tons : translations.tonsFromThisLoad }}</th>
                      <th v-if="!section.selected">{{ translations.link }}</th>
                      <th>{{ translations.details }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="link in section.links">
                      <tr :key="link.movement.id" :class="section.selected ? 'load-row' : 'unload-row'">
                        <td>{{ link.movement.id }}<br>{{ link.movement.registryId || '—' }}</td>
                        <td class="text-no-wrap">{{ toHumanDate(link.movement.date) }}</td>
                        <td>{{ deletionPileLabel(link.movement.pile) }}</td>
                        <td>{{ toFloat(section.selected ? link.movement.load : link.movement.unload) }}</td>
                        <td>{{ section.selected ? toFloat(link.movement.remainder) : link.allocatedTonsFromLoad === null ? translations.notApplicable : toFloat(link.allocatedTonsFromLoad) }}</td>
                        <td v-if="!section.selected">
                          <div v-if="link.allocatedTonsFromLoad !== null">{{ translations.consumesThisLoad }}</div>
                          <div v-if="link.transferOrigin">{{ translations.transferOrigin }}</div>
                        </td>
                        <td>
                          <v-btn small text color="main" :aria-expanded="deletion.expanded.includes(link.movement.id) ? 'true' : 'false'"
                                 :aria-controls="'deletion-details-' + link.movement.id" @click="toggleDeletionDetails(link.movement.id)">
                            {{ translations.details }}
                          </v-btn>
                        </td>
                      </tr>
                      <tr v-if="deletion.expanded.includes(link.movement.id)" :id="'deletion-details-' + link.movement.id" :key="'details-' + link.movement.id">
                        <td :colspan="section.selected ? 6 : 7" class="pa-3">
                          <v-simple-table dense>
                            <tbody>
                              <tr v-for="field in deletionDetails(link.movement)" :key="field[0]">
                                <th scope="row">{{ field[0] }}</th>
                                <td class="deletion-detail-value">{{ field[1] == null || field[1] === '' ? '—' : field[1] }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </v-simple-table>
              </section>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-btn text color="main" :disabled="deletion.phase === 'deleting'" @click="cancelDeletion()">{{ translations.abort }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="deletion.phase === 'error'" color="main" class="white--text" :disabled="canReadOnly" @click="retryDeletion()">{{ translations.retry }}</v-btn>
            <v-btn v-else-if="deletion.preview" color="main" class="white--text" :disabled="deletion.phase !== 'review' || canReadOnly" :loading="deletion.phase === 'deleting'" @click="deletePileMovement()">{{ translations.confirm }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- save/update dialog -->
      <v-dialog v-model="showDialog" max-width="500px" @click:outside="endEditMode" @keydown.enter="disabled ? null : save()">
        <v-card class="pa-2">
          <v-card-title>
            <v-row class="ma-2" justify="center">
              <h3 class="main--text">{{ isUpdate ? translations.updateRecord : translations.createRecord }}</h3>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-container>
              <!-- date picker -->
              <v-row v-if="showDialog">
                <v-col cols="4">
                  <v-subheader>{{ translations.date }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-menu v-model="datePicker" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="formattedDate" readonly dense outlined hide-details v-bind="attrs" v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItemRef.date" no-title scrollable @input="datePicker = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- manufacturer -->
              <v-row v-if="pile && isLoad">
                <v-col cols="4">
                  <v-subheader>{{ translations.manufacturer }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-autocomplete solo dense hide-details v-if="showDialog" v-model="editedItemRef.manufacturer" :search-input.sync="manufacturerOptions.params.manufacturerName" :loading="$apollo.queries.manufacturers.loading" :items="editedItemRef.manufacturer ? [editedItemRef.manufacturer, ...manufacturerOptions.list] : manufacturerOptions.list" item-text="name" item-value="id" return-object :disabled="!isUpdate && editedItemRef.type === 'UNLOAD'" clearable>
                    <template v-slot:append-item>
                      <v-row v-if="manufacturerOptions.next && !$apollo.queries.manufacturers.loading" v-intersect="onIntersect" id="manufacturer" class="ma-0" justify="center">
                        <v-progress-circular indeterminate color="main" :size="20"></v-progress-circular>
                      </v-row>
                    </template>

                    <template v-slot:no-data>
                      <v-list-item>
                        {{ translations.noManufacturerFoundWThatName }}
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- local unit -->
              <v-row v-if="pile && pileTypeIsLimited && isLoad">
                <v-col cols="4">
                  <v-subheader>{{ translations.localUnit }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-autocomplete ref="localUnit" solo dense hide-details v-if="showDialog" v-model="editedItemRef.localUnit" :search-input.sync="localUnitOptions.localUnitName" :loading="$apollo.queries.localUnits.loading" :items="localUnitOptions.list" item-text="name" return-object :disabled="!isUpdate && editedItemRef.type === 'UNLOAD'" clearable>
                    <template v-slot:append-item>
                      <v-row v-if="localUnitOptions.next && !$apollo.queries.localUnits.loading" v-intersect="onIntersect" id="localUnit" class="ma-0" justify="center">
                        <v-progress-circular indeterminate color="main" :size="20"></v-progress-circular>
                      </v-row>
                    </template>

                    <template v-slot:no-data>
                      <v-list-item>
                        {{ translations.noLocalUnitFoundWThatName }}
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- local unit / project (SPECIAL) -->
              <v-row v-if="pile && pile.type === 'SPECIAL' && isLoad">
                <v-col cols="4">
                  <v-subheader>{{ translations.localUnitProject }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-select solo dense hide-details v-if="showDialog" v-model="selectedLocalUnitProject" :items="availablePileLocalUnitProjects" return-object clearable>
                    <template v-slot:item="data">
                      <span>{{ data.item.localUnit ? `${data.item.localUnit.name}` : `${data.item.project.label}` }}</span>
                    </template>
                    <template v-slot:selection="data">
                      <span>{{ data.item.localUnit ? `${data.item.localUnit.name}` : `${data.item.project.label}` }}</span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- local unit / project (SPECIALv2) -->
              <v-row v-if="pile && pile.type === 'SPECIALv2' && isLoad">
                <v-col cols="4">
                  <v-subheader>{{ translations.localUnitProject }}</v-subheader>
                </v-col>
                <v-col :cols="colNumber">
                  <v-menu v-model="openMenu" :value="openMenu" allow-overflow offset-y max-height="400px" :key="forceUpdate" :close-on-content-click="false">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-on="on"
                        @click.stop="openMenu = true"
                        outlined
                        dense
                        hide-details
                        autocomplete="off"
                        v-model="search"
                        return-object
                        clearable
                        @click:clear="clearSearchText()"
                        @blur="textBlur()"
                        @input="value => inputHandler(value)">
                      </v-text-field>
                    </template>

                    <div style="max-height: 300px; z-index: 2; background-color: #fff" class="scroll-bar">
                      <div class="d-flex align-center justify-space-between px-2 pt-2">
                        <div class="d-flex align-center mr-2">
                          <!-- localUnit menu -->
                          <v-btn @click.prevent=";(activeMenuList = 'localUnits'), (changedProjectList = true)" x-small class="mr-2" :class="[activeMenuList === 'localUnits' ? 'white--text' : '']" :outlined="activeMenuList !== 'localUnits'" color="main">
                            {{ translations.localUnit }}
                          </v-btn>

                          <!-- project menu -->
                          <v-btn @click.prevent=";(activeMenuList = 'projects'), (changedProjectList = true)" x-small :class="[activeMenuList === 'projects' ? 'white--text' : '']" :outlined="activeMenuList !== 'projects'" color="main">
                            {{ translations.projects }}
                          </v-btn>
                        </div>
                        <v-btn x-small icon @click.stop="closeMenuHandler()">
                          <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                        </v-btn>
                      </div>

                      <v-list dense>
                        <!-- local unit list -->
                        <template v-if="activeMenuList === 'localUnits'">
                          <v-subheader>{{ translations.localUnit }}</v-subheader>
                          <v-list-item v-for="localUnit in localUnitOptions.list" :key="localUnit.name" @click="changeLocalUnit(localUnit)">
                            <v-list-item-content>
                              <v-list-item-title>{{ localUnit.name }}</v-list-item-title>
                              <v-list-item-subtitle>{{ localUnit.description }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-if="!localUnitOptions.loading && localUnitOptions.next">
                            <div class="d-flex align-center justify-center" style="width: 100%">
                              <v-progress-circular v-intersect.quiet="onIntersectList" indeterminate color="main" size="24"></v-progress-circular>
                            </div>
                          </v-list-item>
                          <v-list-item v-if="!localUnitOptions.loading && !localUnitOptions.next && localUnitOptions.list.length === 0">
                            <v-list-item-content>
                              <v-list-item-title>{{ translations.noResultsFound }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>

                        <!-- project list -->
                        <template v-else-if="activeMenuList === 'projects'">
                          <v-subheader>{{ translations.projects }}</v-subheader>
                          <v-list-item v-for="project in projectOptions.list" @click="changeProject(project)" :key="project.keyCode">
                            <v-list-item-content>
                              <v-list-item-title>{{ project.keyCode }}</v-list-item-title>
                              <v-list-item-subtitle>{{ project.label }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-if="!projectOptions.loading && projectOptions.next">
                            <div class="d-flex align-center justify-center" style="width: 100%">
                              <v-progress-circular v-intersect.quiet="onIntersectList" indeterminate color="main" size="24"></v-progress-circular>
                            </div>
                          </v-list-item>
                          <v-list-item v-if="!projectOptions.loading && !projectOptions.next && projectOptions.list.length === 0">
                            <v-list-item-content>
                              <v-list-item-title>{{ translations.noResultsFound }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-list>
                    </div>
                  </v-menu>
                </v-col>

                <!-- Part -->
                <v-col v-if="hasParts" cols="3">
                  <v-select v-model="editedItemRef.part" return-object :items="projectParts()" item-text="keyCode" :no-data-text="translations.noPartsAvailable" append-icon="" item-value="id" dense outlined hide-details clearable @click:clear="editedItemRef.part = null">
                    <template v-slot:item="data">
                      <small>{{ `${data.item.keyCode} - ${data.item.description}` }}</small>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- carrier -->
              <v-row v-if="showCarrier">
                <v-col cols="4">
                  <v-subheader>{{ translations.carrier }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-autocomplete ref="carrier" solo dense hide-details v-if="showDialog" v-model="editedItemRef.carrier" :search-input.sync="carrierOptions.manufacturerName" :loading="$apollo.queries.carriers.loading" :items="carrierOptions.list" item-text="name" return-object clearable>
                    <template v-slot:append-item>
                      <v-row v-if="carrierOptions.next && !$apollo.queries.carriers.loading" v-intersect="onIntersect" id="carrier" class="ma-0" justify="center">
                        <v-progress-circular indeterminate color="main" :size="20"></v-progress-circular>
                      </v-row>
                    </template>

                    <template v-slot:no-data>
                      <v-list-item>
                        {{ translations.noCarrierFoundWThatName }}
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- destination pile/category -->
              <v-row v-if="pile && ['SPECIAL', 'SPECIALv2'].includes(pile.type) && isUnload && !isUpdate">
                <v-col cols="4">
                  <v-subheader>{{ translations.destination }}</v-subheader>
                </v-col>

                <v-col cols="8">
                  <v-menu v-model="openDestinationMenu" :value="openDestinationMenu" allow-overflow offset-y max-height="400px" :key="forceUpdateDestination" :close-on-content-click="false">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-on="on"
                        @click.stop="openDestinationMenu = true"
                        dense
                        solo
                        hide-details
                        autocomplete="off"
                        v-model="searchDestination"
                        return-object
                        clearable
                        @click:clear="clearText()"
                        @blur="destinationTextBlur()"
                        @input="value => destinationInputHandler(value)"
                        :append-icon="icons.mdiTriangleSmallDown">
                      </v-text-field>
                    </template>

                    <div style="max-height: 300px; z-index: 3; background-color: #fff" class="scroll-bar">
                      <div class="d-flex align-center justify-space-between px-2 pt-2">
                        <div class="d-flex align-center mr-2">
                          <!-- piles menu -->
                          <v-btn @click.prevent=";(activeDestinationMenuList = 'piles'), (changedList = true)" x-small class="mr-2" :class="[activeDestinationMenuList === 'piles' ? 'white--text' : '']" :outlined="activeDestinationMenuList !== 'piles'" color="main">
                            {{ translations.piles }}
                          </v-btn>

                          <!-- category menu -->
                          <v-btn @click.prevent=";(activeDestinationMenuList = 'categories'), (changedList = true)" x-small :class="[activeDestinationMenuList === 'categories' ? 'white--text' : '']" :outlined="activeDestinationMenuList !== 'categories'" color="main">
                            {{ translations.categories }}
                          </v-btn>
                        </div>
                        <v-btn x-small icon @click.stop="closeDestinationMenuHandler()">
                          <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                        </v-btn>
                      </div>

                      <v-list>
                        <!-- piles list -->
                        <template v-if="activeDestinationMenuList === 'piles'">
                          <v-subheader>{{ translations.piles }}</v-subheader>
                          <v-list-item v-for="pile in pileOptions.list" :key="pile.id" @click="changePile(pile)">
                            <v-list-item-content>
                              <v-list-item-title>{{ pile.letter }}</v-list-item-title>
                              <v-list-item-subtitle>{{ pile.description }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-if="!pileOptions.loading && pileOptions.next">
                            <div class="d-flex align-center justify-center" style="width: 100%">
                              <v-progress-circular v-intersect.quiet="onDestinationIntersectList" indeterminate color="main" size="24"></v-progress-circular>
                            </div>
                          </v-list-item>
                          <v-list-item v-if="!pileOptions.loading && !pileOptions.next && pileOptions.list.length === 0">
                            <v-list-item-content>
                              <v-list-item-title>{{ translations.noResultsFound }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>

                        <!-- categories list -->
                        <template v-else-if="activeDestinationMenuList === 'categories'">
                          <v-subheader>{{ translations.categories }}</v-subheader>
                          <v-list-item v-for="category in categoryOptions.list" @click="changeCategory(category)" :key="category.keyCode">
                            <v-list-item-content>
                              <v-list-item-title>{{ category.keyCode }}</v-list-item-title>
                              <v-list-item-subtitle>{{ category.label }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-if="!categoryOptions.loading && categoryOptions.next">
                            <div class="d-flex align-center justify-center" style="width: 100%">
                              <v-progress-circular v-intersect.quiet="onDestinationIntersectList" indeterminate color="main" size="24"></v-progress-circular>
                            </div>
                          </v-list-item>
                          <v-list-item v-if="!categoryOptions.loading && !categoryOptions.next && categoryOptions.list.length === 0">
                            <v-list-item-content>
                              <v-list-item-title>{{ translations.noResultsFound }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-list>
                    </div>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- load/unload value -->
              <v-row>
                <v-col cols="4">
                  <v-subheader>{{ isLoad ? translations.load : translations.unload }}</v-subheader>
                </v-col>
                <v-col cols="8">
                  <v-text-field ref="load" v-if="showDialog && editedItemRef.type === 'LOAD'" outlined dense hide-details type="number" :rules="[rules.min]" v-model="editedItemRef.load"></v-text-field>
                  <v-text-field ref="unload" v-else-if="showDialog && editedItemRef.type === 'UNLOAD'" outlined dense hide-details type="number" :rules="[rules.min]" v-model="editedItemRef.unload"></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <!-- actions -->
          <v-card-actions>
            <v-btn text color="main" @click="endEditMode()">{{ translations.abort }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="main" class="white--text" :disabled="disabled" @click="save()">{{ translations.confirm }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- certificate dialog -->
      <certificate-dialog v-if="showCertificateDialog" :open="showCertificateDialog" :pileLocalUnitProjects="pileLocalUnitProjects" :extended-options="extendedOptions && !isInactivePile" @close-certificate-dialog="closeCertificateDialog()" @checkbox-updated-event="receiveCheckboxUpdatedEvent" @upload-certificate="receiveUploadCertificateEvent"></certificate-dialog>
    </div>
  </main>
</template>

<script>
import { translations } from '@/utils/common'
import { MomentMixin } from '@/utils/mixins/MomentMixin.js'
import { cloneDeep } from 'lodash'
import debounce from 'lodash/debounce'
import { mapState } from 'vuex'

import {
  mdiArrowDown,
  mdiArrowUp,
  mdiDelete,
  mdiDownload,
  mdiEqual,
  mdiFileOutline,
  mdiFilePdfOutline,
  mdiOpenInNew,
  mdiPlus,
  mdiPrinter,
  mdiSquareEditOutline,
  mdiTriangleSmallDown,
  mdiUpload
} from '@mdi/js'

import pileQuery from '@/graphql/Pile.gql'
import manufacturersQuery from '@/graphql/ManufacturersAndCarriers.gql'
import carriersQuery from '@/graphql/Carriers.gql'
import localUnitsQuery from '@/graphql/LocalUnits.gql'
import updatePileMovementsMutation from '@/graphql/UpdatePileMovement.gql'
import deletePileMovementMutation from '@/graphql/DeletePileMovement.gql'
import saveLoadMutation from '@/graphql/SaveLoad.gql'
import saveUnloadMutation from '@/graphql/SaveUnload.gql'
import projectsQuery from '../graphql/Projects.gql'
import pilesQuery from '../graphql/Piles.gql'
import saveCertificate from '../graphql/SaveCertificate.gql'
import updateDocument from '../graphql/updateDocument.gql'
import pileLocalUnitProjects from '../graphql/PileLocalUnitProjects.gql'

import { getMobileDriverShowDDTButton, getPileTypes } from '@/utils/api/config'
import { ddtReport, pileMovementReport } from '@/utils/api/reportsApi'

import SimpleDateHeader from '@/components/SimpleDateHeader.vue'
import PieChart from '@/components/Charts/PieChart.vue'
import CertificateDialog from '@/components/CertificateDialog.vue'
import SmartTable from '@/components/SmartTable'

import PileMovementService from '@/service/pileMovement/PileMovementService'

export default {
  name: 'PileMovements',

  components: {
    SimpleDateHeader,
    PieChart,
    CertificateDialog,
    SmartTable
  },

  async created() {
    this.pileId = this.$route?.query?.pileId
    this.operation = this.$route?.query?.op
    this.currentDate = this.getSimpleDay().substr(0, 10)
    this.categoryOptions.params.dateStr = this.currentDate
    this.projectOptions.params.dateStr = this.currentDate
    this.ddt = getMobileDriverShowDDTButton()
    this.pileTypes = getPileTypes()
  },

  beforeDestroy() {
    this.deletion = null
  },

  apollo: {
    pile: {
      query: pileQuery,
      variables() {
        return {
          pileId: this.pileId
        }
      },
      fetchPolicy: 'network-only',
      async result({ data }) {
        this.plantId = data.pile.plant.id
        this.pileOptions.params.plantId = this.plantId
      },
      skip() {
        return this.pileId ? false : true
      }
    },

    manufacturers: {
      query: manufacturersQuery,
      variables() {
        return {
          page: this.manufacturerOptions.page,
          pageSize: this.listPageSize,
          sort: 'name',
          order: 'asc',
          manufacturerName: this.manufacturerSearchName
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        if (data) {
          const { content, hasNext } = data.manufacturers
          this.manufacturerOptions.next = hasNext
          this.manufacturerOptions.list = this.manufacturerOptions.page === 1 ? content : this.manufacturerOptions.list.concat(content)
        }
      },
      update: data => data.content
    },

    carriers: {
      query: carriersQuery,
      variables() {
        return {
          page: 1,
          pageSize: this.listPageSize,
          sort: 'name',
          order: 'asc',
          carrierName: this.carrierToSearch
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const { content, hasNext } = data.manufacturers
        this.carrierOptions.next = hasNext
        this.carrierOptions.list = content
      },
      update: data => data.content
    },

    localUnits: {
      query: localUnitsQuery,
      variables() {
        return {
          page: 1,
          pageSize: this.listPageSize,
          sort: 'name',
          order: 'asc',
          localUnitName: this.localUnitToSearch
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const { content, hasNext } = data.localUnits
        this.localUnitOptions.next = hasNext
        this.localUnitOptions.list = content
      },
      update: data => data.content
    },

    piles: {
      query: pilesQuery,
      variables() {
        return {
          plantId: this.plantId,
          page: this.page,
          pageSize: this.pageSize
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const { content, hasNext } = data.piles
        this.pileOptions.next = hasNext
        this.pileOptions.list = content.filter(p => p.id !== this.pile.id)
      },
      update: data => data.content,
      skip() {
        return this.plantId ? false : true
      }
    },

    categories: {
      query: projectsQuery,
      variables() {
        return {
          page: 1,
          pageSize: this.pageSize,
          sort: 'keyCode',
          order: 'asc',
          activeStatus: true,
          fetchCategory: true,
          fetchNonCategory: false,
          dateStr: this.currentDate ? this.formatDate(this.currentDate) : null,
          filter: this.categoryOptions.params.filter
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const { content, hasNext } = data.projects
        this.categoryOptions.next = hasNext
        this.categoryOptions.list = content
      },
      update: data => data.content,
      skip() {
        return this.currentDate ? false : true
      }
    },

    projects: {
      query: projectsQuery,
      variables() {
        return {
          page: 1,
          pageSize: this.pageSize,
          sort: 'keyCode',
          order: 'asc',
          activeStatus: true,
          fetchCategory: false,
          fetchNonCategory: true,
          dateStr: this.currentDate ? this.formatDate(this.currentDate) : null,
          filter: this.categoryOptions.params.filter
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const { content, hasNext } = data.projects
        this.projectOptions.next = hasNext
        this.projectOptions.list = content
      },
      update: data => data.content,
      skip() {
        return this.currentDate ? false : true
      }
    },

    pileLocalUnitProjects: {
      query: pileLocalUnitProjects,
      variables() {
        return {
          pileId: this.pileId
        }
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        this.pileLocalUnitProjects = this.mapCertificates(data.pileLocalUnitProjects)
      },
      skip() {
        return this.pileId ? false : true
      }
    }
  },

  data: () => ({
    pileId: null,
    plantId: null,
    page: 1,
    pageSize: 10,
    pileLocalUnitProjects: [],
    editMode: false,
    editedItem: null,
    editedItemRef: null,
    listPageSize: 10,
    showDialog: false,
    deletion: null,
    isLoad: false,
    isUnload: false,
    isUpdate: false,
    certificateFile: null,
    certificateName: '',
    operation: null,
    showErrorAlert: false,
    errorMessage: '',
    showSuccessAlert: false,
    successMessage: '',
    datePicker: false,
    previous: {
      path: '/piles',
      query: {
        plantId: null
      }
    },
    rules: {
      min: value => value > 0
    },
    currentDate: null,
    manufacturerOptions: {
      list: [],
      page: 1,
      next: true,
      params: {
        manufacturerName: null
      }
    },
    localUnitOptions: {
      list: [],
      page: 1,
      next: true,
      query: localUnitsQuery,
      alias: 'localUnits',
      params: {
        sort: 'name',
        order: 'asc',
        localUnitName: null
      }
    },
    carrierOptions: {
      list: [],
      page: 1,
      next: true,
      query: carriersQuery,
      alias: 'manufacturers',
      params: {
        sort: 'name',
        order: 'asc',
        manufacturerName: null
      }
    },
    pileOptions: {
      list: [],
      page: 1,
      next: true,
      query: pilesQuery,
      alias: 'piles',
      loading: false,
      params: {
        plantId: null,
        filter: null
      }
    },
    categoryOptions: {
      list: [],
      page: 1,
      next: true,
      query: projectsQuery,
      alias: 'projects',
      loading: false,
      params: {
        sort: 'keyCode',
        order: 'asc',
        activeStatus: true,
        fetchCategory: true,
        fetchNonCategory: false,
        dateStr: null,
        filter: null
      }
    },
    projectOptions: {
      list: [],
      page: 1,
      next: true,
      query: projectsQuery,
      alias: 'projects',
      loading: false,
      params: {
        sort: 'keyCode',
        order: 'asc',
        activeStatus: true,
        fetchCategory: false,
        fetchNonCategory: true,
        dateStr: null,
        filter: null
      }
    },
    ddt: false,
    availablePileLocalUnitProjects: [],
    selectedLocalUnitProject: null,
    openDestinationMenu: false,
    forceUpdateDestination: 0,
    searchDestination: null,
    activeDestinationMenuList: 'piles',
    changedList: false,
    showCertificateDialog: false,
    isSelecting: false,
    extendedOptions: false,
    openMenu: false,
    forceUpdate: 0,
    search: null,
    activeMenuList: 'localUnits',
    changedProjectList: false,
    selectedPileLocalUnitProject: null,
    selectedAttachmentMovement: null,
    pileTypes: []
  }),

  mixins: [MomentMixin],

  methods: {
    ddtReport,

    toHumanDate(date) {
      return this.momentDate(date, 'DD/MM/YYYY')
    },

    toFloat(value) {
      return parseFloat(value).toFixed(2)
    },

    pileMovementReportWrap() {
      // export with the same filters of the table
      const filters = this.$refs.smartTable ? this.toSearchParams(this.$refs.smartTable.getParams()) : {}

      // the report url skips falsy values: send the certificate as a string to keep the 'false' filter
      if (filters.certificate !== undefined) {
        filters.certificate = String(filters.certificate)
      }

      pileMovementReport({
        ...filters,
        pileId: this.pile.id,
        pileType: this.pile.type,
        year: this.pile.year,
        accountId: this.userInfo?.account?.id
      })
    },

    resetFields() {
      this.editMode = false
      this.isUpdate = false
      this.isLoad = false
      this.isUnload = false
      this.editedItem = null
      this.editedItemRef = null
      this.showDialog = false
      this.datePicker = false
      this.search = null
      this.searchDestination = null
      this.selectedLocalUnitProject = null
      this.availablePileLocalUnitProjects = []
    },

    async updatePileMovement(pileMovement) {
      try {
        await this.$apollo.mutate({
          mutation: updatePileMovementsMutation,
          variables: {
            pileMovementDTO: {
              id: pileMovement.id,
              carrier: pileMovement.carrier ? { id: pileMovement.carrier.id } : null,
              manufacturer: pileMovement.manufacturer ? { id: pileMovement.manufacturer.id } : null,
              localUnit: pileMovement.localUnit ? { id: pileMovement.localUnit.id } : null,
              project: pileMovement.project ? { id: pileMovement.project.id } : null,
              part: pileMovement.part ? { id: pileMovement.part.id } : null,
              dateStr: this.formatDate(pileMovement.date),
              value: pileMovement.type === 'LOAD' ? pileMovement.load : pileMovement.unload
            }
          }
        })

        this.reloadPileMovements()
        this.$apollo.queries.pile.refetch()
        this.$apollo.queries.pileLocalUnitProjects.refetch()
        this.resetFields()
      } catch (error) {
        this.endEditMode()
        console.error(error)
        this.errorMessage = error.message.includes('negative') || error.message.includes('insufficient') ? this.translations.insufficientTonsError : this.translations.updateError
        this.showErrorAlert = true
      }
    },

    startEditMode(pileMovement) {
      this.editMode = true
      this.isUpdate = true
      this.isLoad = pileMovement.type === 'LOAD'
      this.isUnload = pileMovement.type === 'UNLOAD'
      this.availablePileLocalUnitProjects = this.setAvailablePileLocalUnitProjects()
      this.editedItem = cloneDeep(pileMovement)
      this.editedItemRef = pileMovement
      this.editedItemRef.dateStr = this.toHumanDate(this.editedItem.date)
      this.editedItemRef.date = this.formatDate(this.editedItem.date)
      this.selectedLocalUnitProject = this.pile && ['SPECIAL', 'SPECIALv2'].includes(this.pile.type) ? this.getSelectedLocalUnitProject(pileMovement) : null
      this.showDialog = true

      // select current localUnit
      if (this.editedItem.localUnit && this.editedItem.type === 'LOAD' && this.pileTypeIsLimited) {
        setTimeout(() => {
          this.$refs.localUnit.focus()
          this.localUnitToSearch = this.editedItem.localUnit.name
        }, 200)
      }

      // select current carrier
      if (this.showCarrier && this.editedItem.carrier) {
        setTimeout(() => {
          this.$refs.carrier.focus()
          this.carrierToSearch = this.editedItem.carrier.name
        }, 200)
      }

      // focus on value field
      setTimeout(() => {
        this.$refs[this.editedItem.type.toLowerCase()].focus()
      }, 200)
    },

    setAvailablePileLocalUnitProjects() {
      if (this.isLoad) {
        return this.pileLocalUnitProjects
      } else {
        let items = cloneDeep(this.pileLocalUnitProjects)
        return items.filter(i => i.certificate)
      }
    },

    getSelectedLocalUnitProject(pileMovement) {
      for (let item of this.pileLocalUnitProjects) {
        if (pileMovement.localUnit && item.localUnit && item.localUnit.id === pileMovement.localUnit.id) {
          return item
        }
        if (pileMovement.project && item.project && item.project.id === pileMovement.project.id) {
          if (pileMovement.part) {
            if (item.part && item.part.id === pileMovement.part.id) {
              return item
            }
          } else {
            return item
          }
        }
      }
      return null
    },

    endEditMode() {
      // reload the table to discard the changes made on the edited item
      if (this.isUpdate) {
        this.reloadPileMovements()
      }

      this.resetFields()
    },

    async requestDeletion(movement) {
      if (this.canReadOnly || this.deletion) return
      if (movement.type !== 'LOAD') return this.deletePileMovement(movement.id)

      const attempt = { id: movement.id, phase: 'checking', preview: null, error: null, expanded: [] }
      this.deletion = attempt
      try {
        const preview = await PileMovementService.deletionPreview(attempt.id)
        if (this.deletion !== attempt) return
        if (this.canReadOnly) return this.cancelDeletion()
        attempt.phase = 'review'
        if (preview.linkedUnloads.length) attempt.preview = preview
        else await this.deletePileMovement()
      } catch (error) {
        if (this.deletion !== attempt) return
        attempt.phase = 'error'
        attempt.error = error.message.includes('PILE_MOVEMENT_PREVIEW_FORBIDDEN')
          ? this.translations.linkedUnloadsForbidden : this.translations.linkedUnloadsError
      }
    },

    cancelDeletion() {
      if (this.deletion?.phase === 'deleting') return
      this.deletion = null
    },

    retryDeletion() {
      if (this.deletion?.phase !== 'error') return
      const { id } = this.deletion
      this.deletion = null
      return this.requestDeletion({ id, type: 'LOAD' })
    },

    toggleDeletionDetails(id) {
      const expanded = this.deletion.expanded
      const index = expanded.indexOf(id)
      if (index === -1) expanded.push(id)
      else expanded.splice(index, 1)
    },

    deletionReferenceLabel(reference) {
      return reference ? [reference.id, reference.keyCode, reference.name, reference.label, reference.description].filter(value => value != null && value !== '').join(' · ') : '—'
    },

    deletionPileLabel(pile) {
      return pile ? [pile.id, [pile.letter, pile.year].filter(value => value != null).join('/'), pile.description, pile.type, this.deletionReferenceLabel(pile.plant)].filter(value => value != null && value !== '').join(' · ') : '—'
    },

    deletionDetails(movement) {
      const t = this.translations
      const fields = [
        [t.movementType, movement.type === 'LOAD' ? t.load : t.unload],
        [t.manufacturer, this.deletionReferenceLabel(movement.manufacturer)],
        [t.carrier, this.deletionReferenceLabel(movement.carrier)],
        [t.localUnit, this.deletionReferenceLabel(movement.localUnit)],
        [t.project, this.deletionReferenceLabel(movement.project)],
        [t.part, this.deletionReferenceLabel(movement.part)],
        [t.destination + ' · ' + t.piles, this.deletionPileLabel(movement.destinationPile)],
        [t.destination + ' · ' + t.category, this.deletionReferenceLabel(movement.destinationCategory)],
        [t.transferOrigin, movement.movedFrom],
        [t.transferDestination, movement.movedTo],
        [t.sourceLoads, movement.unloadOperations.join(', ')]
      ]
      if (movement.type === 'LOAD') fields.splice(1, 0, [t.unload + ' ' + t.tons, this.toFloat(movement.unload)])
      const ddt = movement.ddt
      if (ddt) {
        fields.push(
          ['DDT · ' + t.id, ddt.id],
          [t.ddtRegistryId, ddt.registryId],
          ['DDT · ' + t.date, ddt.createdAtStr],
          ['DDT · ' + t.clockInTitle + ' · ' + t.id, ddt.clockInId],
          ['DDT · ' + t.employee, ddt.employeeName],
          ['DDT · ' + t.project, ddt.projectKeyCode],
          ['DDT · ' + t.part, ddt.partKeyCode],
          ['DDT · ' + t.m3, ddt.m3],
          ['DDT · ' + t.additionalInfo, ddt.additionalInfo]
        )
      } else fields.push(['DDT', t.noAssociatedDdt])
      return fields
    },

    async deletePileMovement(pileMovementId = this.deletion?.id) {
      const attempt = this.deletion
      if (pileMovementId == null || this.canReadOnly) return
      if (attempt) {
        if (attempt.phase !== 'review') return
        attempt.phase = 'deleting'
      }

      try {
        // delete record
        await this.$apollo.mutate({
          mutation: deletePileMovementMutation,
          variables: { pileMovementId }
        })

        if (attempt && this.deletion !== attempt) return
        this.reloadPileMovements()
        this.$apollo.queries.pile.refetch()
        this.$apollo.queries.pileLocalUnitProjects.refetch()
        if (attempt) this.deletion = null
      } catch (error) {
        if (attempt && this.deletion !== attempt) return
        console.error(error)
        this.errorMessage = error.message.includes('negative') ? this.translations.insufficientTonsError : this.translations.deleteError
        if (attempt) {
          attempt.phase = 'error'
          attempt.error = this.errorMessage
        } else {
          this.showErrorAlert = true
        }
      }
    },

    saveLoad() {
      this.showDialog = true
      this.isLoad = true
      this.editedItemRef = this.buildPileMovement('LOAD')
      this.availablePileLocalUnitProjects = this.setAvailablePileLocalUnitProjects()
    },

    saveUnload() {
      this.showDialog = true
      this.isUnload = true
      this.editedItemRef = this.buildPileMovement('UNLOAD')
      this.availablePileLocalUnitProjects = this.setAvailablePileLocalUnitProjects()
    },

    async save() {
      // update record
      if (this.isUpdate) {
        return await this.updatePileMovement(this.editedItemRef)
      }

      // save new record
      try {
        await this.$apollo.mutate({
          mutation: this.isLoad ? saveLoadMutation : saveUnloadMutation,
          variables: this.getSaveRequestDTO()
        })
        this.reloadPileMovements()
        this.$apollo.queries.pile.refetch()
        this.$apollo.queries.pileLocalUnitProjects.refetch()
      } catch (error) {
        console.error(error)
        this.errorMessage = error.message.includes('insufficient') ? this.translations.insufficientTonsError : error.message.includes('exceeded') ? this.translations.exceededTonsError : this.translations.saveError
        this.showErrorAlert = true
      }

      this.resetFields()
    },

    mapCertificates(pileLocalUnitProjects) {
      if (!pileLocalUnitProjects) {
        return []
      }
      for (let plp of pileLocalUnitProjects) {
        // certificateDto is null when the certificate file is missing
        if (plp.certificate && plp.certificateDto) {
          if (pileLocalUnitProjects.length === 1 && this.pile.type != 'SPECIALv2') {
            this.certificateFile = `data:application/pdf;base64,${plp.certificateDto.certificate}`
            this.certificateName = plp.certificateDto.filename
          } else {
            // FIXME: use certificateDto fields directly
            plp['certificateBase64'] = `data:application/pdf;base64,${plp.certificateDto.certificate}`
            plp['certificateName'] = plp.certificateDto.filename
          }
        }
      }
      return pileLocalUnitProjects
    },

    getSaveRequestDTO() {
      if (this.isLoad) {
        return {
          loadDTO: {
            carrier: this.editedItemRef?.carrier ? { id: this.editedItemRef.carrier.id } : null,
            localUnit: this.editedItemRef?.localUnit ? { id: this.editedItemRef?.localUnit?.id } : null,
            project: this.editedItemRef?.project ? { id: this.editedItemRef.project.id } : null,
            part: this.editedItemRef?.part ? { id: this.editedItemRef.part.id } : null,
            manufacturer: this.editedItemRef?.manufacturer ? { id: this.editedItemRef.manufacturer.id } : null,
            pile: { id: this.pile?.id },
            dateStr: this.formatDate(this.editedItemRef.date),
            value: this.editedItemRef?.load
          }
        }
      }
      return {
        unloadDTO: {
          carrier: this.editedItemRef?.carrier ? { id: this.editedItemRef.carrier.id } : null,
          localUnit: this.editedItemRef?.localUnit ? { id: this.editedItemRef?.localUnit?.id } : null,
          project: this.editedItemRef?.project ? { id: this.editedItemRef.project.id } : null,
          part: this.editedItemRef?.part ? { id: this.editedItemRef.part.id } : null,
          pile: { id: this.pile?.id },
          dateStr: this.formatDate(this.editedItemRef.date),
          value: this.editedItemRef?.unload,
          destinationPile: this.editedItemRef?.destinationPile ? { id: this.editedItemRef.destinationPile.id } : null,
          destinationCategory: this.editedItemRef?.destinationCategory ? { id: this.editedItemRef.destinationCategory.id } : null
        }
      }
    },

    buildPileMovement(type) {
      return {
        manufacturer: type === 'UNLOAD' || !this.pileTypeIsLimited ? this.pile?.manufacturer : null,
        localUnit: this.pile?.type === 'STANDARD' ? this.pile?.pileLocalUnitProjects[0].localUnit : null,
        project: null,
        part: null,
        carrier: null,
        pile: { id: this.pile?.id },
        load: 0,
        unload: 0,
        dateStr: this.toHumanDate(this.getSimpleDay()),
        date: this.formatDate(this.getSimpleDay()),
        type: type,
        destinationPile: null,
        destinationCategory: null
      }
    },

    async onIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.fetchMore(entries[0].target.id)
      }
    },

    headers() {
      const headers = []

      headers.push({
        type: 'LABEL',
        name: 'id',
        label: translations.id,
        sort: 'id'
      })

      headers.push({
        type: 'SELECT',
        name: 'types',
        label: translations.movementType,
        selectItems: [
          { text: translations.load, value: 'LOAD' },
          { text: translations.unload, value: 'UNLOAD' }
        ],
        sort: 'type'
      })

      headers.push({
        type: 'TEXT',
        name: 'registryId',
        label: translations.registryId,
        sort: 'registryId'
      })

      headers.push({
        type: 'DATE-RANGE',
        name: 'date',
        label: translations.date,
        sort: 'date'
      })

      headers.push({
        type: 'TEXT',
        name: 'manufacturer',
        label: translations.manufacturer,
        sort: 'm.name'
      })

      headers.push({
        type: 'TEXT',
        name: 'localUnit',
        label: this.isSpecial ? translations.localUnitProject : translations.localUnit,
        sort: this.isSpecial ? null : 'lu.name'
      })

      if (this.isSpecial) {
        headers.push({
          type: 'TEXT',
          name: 'part',
          label: translations.part,
          sort: 'pa.keyCode'
        })
      }

      if (this.pile?.type === 'SPECIALv2') {
        headers.push({
          type: 'SELECT',
          name: 'certificate',
          label: translations.certificateColumn,
          selectItems: [
            { text: translations.yes, value: true },
            { text: translations.no, value: false }
          ]
        })
      }

      headers.push({
        type: 'TEXT',
        name: 'carrier',
        label: translations.carrier,
        sort: 'ca.name'
      })

      headers.push({
        type: 'LABEL',
        name: 'load',
        label: translations.load,
        sort: 'load'
      })

      headers.push({
        type: 'LABEL',
        name: 'unload',
        label: translations.unload,
        sort: 'unload'
      })

      headers.push({
        type: 'LABEL',
        name: 'remainder',
        label: translations.remainderWithUnload,
        sort: 'remainder'
      })

      if (this.isSpecial) {
        headers.push({
          type: 'TEXT',
          name: 'destination',
          label: translations.destination
        })
      }

      headers.push({
        type: 'TEXT',
        name: 'unloadedFrom',
        label: translations.unloadedFrom
      })

      if (this.isSpecial && this.ddt) {
        headers.push({
          type: 'TEXT',
          name: 'm3',
          label: translations.m3
        })

        headers.push({
          type: 'TEXT',
          name: 'additionalInfo',
          label: translations.additionalInfo
        })

        headers.push({
          type: 'TEXT',
          name: 'ddtRegistryId',
          label: translations.ddtRegistryId
        })
      }

      // attachment
      headers.push({
        type: 'LABEL',
        name: 'attachment',
        label: translations.attachment
      })

      // actions
      headers.push({
        type: 'LABEL',
        name: 'actions',
        label: ''
      })

      return headers
    },

    searchFunction(params) {
      return PileMovementService.search(this.toSearchParams(params))
        .then(response => response ? { ...response, content: this.mapPileMovements(response.content) } : response)
    },

    toSearchParams(params) {
      // copy the params: the smart table keeps its own filters
      const { date, certificate, ...searchParams } = params
      searchParams.pileId = this.pileId

      // date range: a single selected day filters that day only
      if (date && date.length > 0) {
        searchParams.dateFromStr = date[0]
        searchParams.dateToStr = date.length > 1 ? date[1] : date[0]
      }

      // certificate: filter only when exactly one value is selected
      if (certificate && certificate.length === 1) {
        searchParams.certificate = certificate[0]
      }

      return searchParams
    },

    reloadPileMovements() {
      if (this.$refs.smartTable) {
        this.$refs.smartTable.getTableRows()
      }
    },

    clearText() {
      this.searchDestination = null
      this.editedItemRef.destinationCategory = null
      this.editedItemRef.destinationPile = null
    },

    destinationTextBlur() {
      setTimeout(() => {
        if (this.changedList) {
          this.changedList = false
          return
        } else {
          this.searchDestination = this.getSelectedDestination()
          this.openDestinationMenu = false
          this.refetchDestinations()
        }
      }, 400)
    },

    getSelectedDestination() {
      if (this.editedItemRef) {
        if (this.editedItemRef.destinationPile) {
          return this.editedItemRef.destinationPile.letter
        }
        if (this.editedItemRef.destinationCategory) {
          return this.editedItemRef.destinationCategory.label
        }
      }
      return ''
    },

    destinationInputHandler(value) {
      this.searchDestination = value
      this.pileOptions.params.filter = value
      this.categoryOptions.params.filter = value
      this.refetchDestinations()
    },

    closeDestinationMenuHandler() {
      this.searchDestination = this.getSelectedDestination()
      this.pileOptions.params.filter = this.search
      this.categoryOptions.params.filter = this.search
      this.openDestinationMenu = false
      this.refetchDestinations()
    },

    changePile(value) {
      this.editedItemRef.destinationPile = value
      this.editedItemRef.destinationCategory = null
      this.search = value.letter
      this.openMenu = false
    },

    changeCategory(value) {
      this.editedItemRef.destinationCategory = value
      this.editedItemRef.destinationPile = null
      this.search = value.keyCode
      this.openMenu = false
    },

    onDestinationIntersectList(entries, observer, isIntersecting) {
      if (isIntersecting) {
        if (this.activeDestinationMenuList === 'piles') {
          this.fetchMore('pile')
        } else {
          this.fetchMore('category')
        }
      }
    },

    refetchDestinations: debounce(function() {
      this.fetchMore('pile', true)
      this.fetchMore('category', true)
    }, 850),

    async fetchMore(entity, reset = false) {
      if (entity === 'manufacturer') {
        if (!this.$apollo.queries.manufacturers.loading && this.manufacturerOptions.next) {
          this.manufacturerOptions.page++
        }
        return
      }

      // get entity's query options
      let options = this[entity + 'Options']

      if (!reset) {
        options.page++
      } else {
        options.page = 1
        options.next = true
        options.list = []
      }

      const res = await this.$apollo.query({
        query: options.query,
        variables: {
          page: options.page,
          pageSize: this.pageSize,
          ...options.params
        },
        fetchPolicy: 'network-only'
      })
      options.next = res.data[options.alias].hasNext
      options.list.push(...res.data[options.alias].content)

      // exclude current pile from list
      if (entity === 'pile') {
        options.list = options.list.filter(i => i.id !== this.pile.id)
      }
    },

    openCertificateDialog() {
      this.showCertificateDialog = true
    },

    closeCertificateDialog() {
      this.showCertificateDialog = false
    },

    mapPileMovements(pileMovements) {
      return pileMovements.map(pm => {
        pm.key = pm.id
        pm.meta = { className: pm.type === 'UNLOAD' ? 'unload-row' : pm.unloaded ? 'load-row' : '' }
        pm.destination = pm.destinationPile ? pm.destinationPile.letter : pm.destinationCategory ? pm.destinationCategory.keyCode : '-'
        pm.ddt = pm.ddt ? pm.ddt : { id: null, m3: null, additionalInfo: null }
        return pm
      })
    },

    hasDocument(pileMovement) {
      const pileLocalUnitProject = this.getPileLocalUnitProject(pileMovement)
      return pileLocalUnitProject ? pileLocalUnitProject.document : null
    },

    isCertified(pileMovement) {
      const pileLocalUnitProject = this.getPileLocalUnitProject(pileMovement)
      return pileLocalUnitProject ? pileLocalUnitProject.certificate : null
    },

    getPileLocalUnitProject(pileMovement) {
      let res = this.pileLocalUnitProjects.filter(i => (i.localUnit ? i.localUnit.id === pileMovement.localUnit?.id : i.project ? i.project.id === pileMovement.project?.id && i.part?.id === pileMovement.part?.id : false))

      return res.length ? res[0] : null
    },

    handleFileUpload(pileMovement) {
      this.selectedPileLocalUnitProject = pileMovement.isPileLocalUnitProject ? pileMovement : this.getPileLocalUnitProject(pileMovement)

      // the movement's local unit / project (and part) is not one of the pile's origins
      if (!this.selectedPileLocalUnitProject) {
        this.errorMessage = this.translations.certificateOriginNotFound
        this.showErrorAlert = true
        return
      }

      this.isSelecting = true

      // after obtaining the focus when closing the FilePicker, return the button state to normal
      window.addEventListener(
        'focus',
        () => {
          this.isSelecting = false
        },
        { once: true }
      )

      // trigger click on the FileInput
      this.$refs.uploader.click()
    },

    onFileChanged(e) {
      const file = e.target.files[0]
      // reset the input, so picking the same file again fires change
      e.target.value = ''

      if (this.selectedPileLocalUnitProject && file) {
        this.selectedPileLocalUnitProject.certificateData = file
        const reader = new FileReader()

        reader.readAsDataURL(file)
        reader.onload = () => {
          this.selectedPileLocalUnitProject.certificateBase64 = reader.result.substr(reader.result.indexOf(',') + 1)

          this.$apollo
            .mutate({
              mutation: saveCertificate,
              variables: {
                pileLocalUnitProjectId: this.selectedPileLocalUnitProject.id,
                certificateBase64: this.selectedPileLocalUnitProject.certificateBase64
              }
            })
            .then(async () => {
              this.selectedPileLocalUnitProject = null
              this.successMessage = this.translations.certificateSaved
              this.showSuccessAlert = true
              await this.$apollo.queries.pileLocalUnitProjects.refetch()
              this.reloadPileMovements()
            })
            .catch(error => {
              console.log(error)
              this.errorMessage = this.translations.saveError
              this.showErrorAlert = true
            })
        }
      }
    },

    clearSearchText() {
      this.search = null
      this.editedItemRef.project = null
      this.editedItemRef.localUnit = null
    },

    textBlur() {
      setTimeout(() => {
        if (this.changedProjectList) {
          this.changedProjectList = false
          return
        } else {
          this.search = this.getSelectedValue()
          this.openMenu = false
          this.refetch()
        }
      }, 400)
    },

    getSelectedValue() {
      if (this.editedItemRef?.localUnit?.name) {
        return this.editedItemRef?.localUnit?.name
      } else if (this.editedItemRef?.project?.keyCode) {
        return this.editedItemRef.project.keyCode
      } else return ''
    },

    inputHandler(value) {
      this.search = value
      this.localUnitOptions.params.localUnitName = value
      this.refetch()
    },

    closeMenuHandler() {
      this.search = this.getSelectedValue()
      this.localUnitOptions.params.localUnitName = this.search
      this.openMenu = false
      this.refetch()
    },

    changeProject(value) {
      this.editedItemRef.project = value?.project ? value.project : value
      this.editedItemRef.part = null
      this.editedItemRef.localUnit = null
      this.search = value.keyCode
      this.openMenu = false
    },

    changeLocalUnit(value) {
      this.editedItemRef.localUnit = value?.localUnit ? value.localUnit : value
      this.editedItemRef.project = null
      this.editedItemRef.part = null
      this.search = value.name
      this.openMenu = false
    },

    projectParts() {
      return this.editedItemRef?.project?.parts
    },

    onIntersectList(entries, observer, isIntersecting) {
      if (isIntersecting) {
        if (this.activeMenuList === 'localUnits') {
          this.fetchMore('localUnit')
        } else {
          this.fetchMore('project')
        }
      }
    },

    refetch: debounce(function() {
      this.fetchMore('localUnit', true)
      this.fetchMore('project', true)
    }, 850),

    async checkboxUpdated(value, item) {
      this.$nextTick(async () => {
        try {
          await this.$apollo.mutate({
            mutation: updateDocument,
            variables: {
              pileLocalUnitProjectId: this.getPileLocalUnitProject(item).id,
              document: value
            }
          })

          // update all pileMovements that share the same PileLocalUnitProject
          await this.$apollo.queries.pileLocalUnitProjects.refetch()
        } catch (error) {
          console.error(error)
        }
      })
    },

    receiveCheckboxUpdatedEvent(data) {
      this.checkboxUpdated(data.value, data.item, true)
    },

    receiveUploadCertificateEvent(data) {
      data.isPileLocalUnitProject = true
      this.handleFileUpload(data)
    },

    handleAttachmentUpload(pileMovement) {
      this.selectedAttachmentMovement = pileMovement
      this.$refs.attachmentUploader.click()
    },

    onAttachmentFileChanged(e) {
      const file = e.target.files[0]
      // reset the input, so picking the same file again fires change
      e.target.value = ''

      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        // only this row changed: pile totals, certificates and DDTs are not refetched
        PileMovementService.saveAttachment({
          pileMovementId: this.selectedAttachmentMovement.id,
          attachmentFilename: file.name,
          attachmentBase64: reader.result.substr(reader.result.indexOf(',') + 1)
        })
          .then(() => this.reloadPileMovements())
          .catch(error => {
            console.error(error)
            this.errorMessage = this.translations.saveError
            this.showErrorAlert = true
          })
      }
    },

    deleteAttachmentConfirmation(pileMovement) {
      if (confirm(translations.areYouSure)) {
        this.deleteAttachment(pileMovement)
      }
    },

    async deleteAttachment(pileMovement) {
      try {
        await PileMovementService.deleteAttachment({ pileMovementId: pileMovement.id })
        this.reloadPileMovements()
      } catch (error) {
        console.error(error)
        this.errorMessage = this.translations.deleteError
        this.showErrorAlert = true
      }
    },

    async downloadAttachment(pileMovement) {
      try {
        const file = await PileMovementService.downloadAttachment({ pileMovementId: pileMovement.id })
        const bytes = Uint8Array.from(atob(file.base64), c => c.charCodeAt(0))
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([bytes]))
        link.download = file.fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(link.href))
      } catch (error) {
        console.error(error)
        this.errorMessage = this.translations.generalError
        this.showErrorAlert = true
      }
    }
  },

  computed: {
    ...mapState({
      userInfo: state => state.auth.userInfo,
      hasReadOnlyCapability: state => state.auth.userInfo.account?.capabilities.some(c => c.name === 'Read-only')
    }),

    isInactivePile() {
      return this.pile?.active === false
    },

    // read only capability or deactivated pile
    canReadOnly() {
      return this.hasReadOnlyCapability || this.isInactivePile
    },

    translations: () => translations,

    showDeletionDialog() {
      return this.deletion?.phase === 'error' || !!this.deletion?.preview
    },

    deletionSections() {
      return [
        { title: this.translations.loadToDelete, selected: true, links: [{ movement: this.deletion.preview.selectedLoad }] },
        { title: this.translations.linkedUnloads, selected: false, links: this.deletion.preview.linkedUnloads }
      ]
    },

    plantName() {
      return this.pile?.plant?.name ? this.pile.plant.name : ''
    },

    disableCertificate() {
      return !this.pileLocalUnitProjects.some(i => i.certificate)
    },

    pileType() {
      if (this.pileTypes.length === 0) return {}

      return this.pileTypes.find(type => type.type === this.pile.type)
    },

    pileTypeIsLimited() {
      if (!this.pileType?.type) return false

      return this.pileType.limited
    },

    isSpecial() {
      return this.pile && ['SPECIAL', 'SPECIALv2'].includes(this.pile.type)
    },

    icons: () => ({
      mdiArrowDown,
      mdiArrowUp,
      mdiDelete,
      mdiDownload,
      mdiEqual,
      mdiFileOutline,
      mdiFilePdfOutline,
      mdiOpenInNew,
      mdiPrinter,
      mdiPlus,
      mdiSquareEditOutline,
      mdiTriangleSmallDown,
      mdiUpload
    }),

    isEmpty() {
      return this.pile?.totalTons === 0
    },

    isClosed() {
      return this.pile?.closed
    },

    formattedDate() {
      return this.editedItemRef ? this.toHumanDate(this.editedItemRef.date) : ''
    },

    manufacturerSearchName() {
      const search = this.manufacturerOptions.params.manufacturerName
      return search && search !== this.editedItemRef?.manufacturer?.name ? search : null
    },

    disabled() {
      if (this.editedItemRef) {
        let pm = this.editedItemRef

        if (pm.type === 'LOAD') {
          if (this.pile.type === 'STANDARD') {
            return !pm.date || !pm.carrier?.id || pm.load <= 0
          } else if (this.pileTypeIsLimited) {
            return !pm.date || !pm.manufacturer?.id || !pm.localUnit?.id || !pm.carrier?.id || pm.load <= 0
          } else if (this.pile.type === 'SPECIAL') {
            return !pm.date || !pm.manufacturer?.id || (pm.ddt && pm.ddt.m3 ? false : !pm.carrier?.id) || pm.load <= 0 || (!pm.localUnit?.id && !pm.project?.id)
          } else {
            return !pm.date || (pm.ddt && pm.ddt.m3 ? false : !pm.carrier?.id) || pm.load <= 0 || (!pm.localUnit?.id && !pm.project?.id)
          }
        } else if (pm.type === 'UNLOAD') {
          if (!['SPECIAL', 'SPECIALv2'].includes(this.pile.type)) {
            return !pm.date || !pm.carrier?.id || pm.unload <= 0
          } else {
            return !pm.date || (!pm.destinationPile?.id && !pm.destinationCategory?.id) || pm.unload <= 0
          }
        }
      }

      return true
    },

    pileLocalUnitProjectName() {
      let name = ''
      if (!this.pileTypeIsLimited) {
        for (let pileLocalUnitProject of this.pileLocalUnitProjects) {
          if (pileLocalUnitProject.localUnit) {
            name = name + (name ? ' - ' : '') + pileLocalUnitProject.localUnit.name
          } else if (pileLocalUnitProject.project) {
            name = name + (name ? ' - ' : '') + (pileLocalUnitProject.project.keyCode + ' ' + pileLocalUnitProject.project.label)
          }
        }
      } else {
        name = '-'
      }
      return name
    },

    showCarrier() {
      return this.editedItemRef?.ddt?.m3 || (this.pile && this.pile.type === 'SPECIAL' && this.isUnload) ? false : true
    },

    hasDocumentOrCertificate() {
      return this.pileLocalUnitProjects.length === 0 || this.pileLocalUnitProjects.some(i => i.document || i.certificate)
    },

    colNumber() {
      if (this.editedItemRef?.project?.parts?.length) {
        return '5'
      }
      return '8'
    },

    hasParts() {
      return this.editedItemRef?.project?.parts?.length ? true : false
    },

    localUnitValue() {
      return this.editedItemRef?.localUnit
    },

    projectValue() {
      return this.editedItemRef?.project
    }
  },

  watch: {
    $route() {
      this.deletion = null
    },

    manufacturerSearchName() {
      this.manufacturerOptions.page = 1
      this.manufacturerOptions.list = []
      this.manufacturerOptions.next = false
    },

    pile() {
      // show movement creation dialog if redirected from pile view
      if (this.operation) {
        if (this.canReadOnly) {
          // no movement creation on a read only pile
        } else if (this.operation === 'load') {
          this.saveLoad()
        } else if (this.operation === 'unload') {
          this.saveUnload()
        }
        this.operation = null
      }

      // set route's query param
      if (!this.previous.query.plantId) {
        this.previous.query.plantId = this.pile.plant.id
      }

      this.extendedOptions = this.pile && this.pile.type === 'SPECIALv2'
    },

    selectedLocalUnitProject() {
      if (this.editedItemRef) {
        if (this.selectedLocalUnitProject) {
          this.editedItemRef.localUnit = this.selectedLocalUnitProject.localUnit ? this.selectedLocalUnitProject.localUnit : null
          this.editedItemRef.project = this.selectedLocalUnitProject.project ? this.selectedLocalUnitProject.project : null
          this.editedItemRef.part = this.selectedLocalUnitProject.part ? this.selectedLocalUnitProject.part : null
        } else {
          this.editedItemRef.localUnit = null
          this.editedItemRef.project = null
          this.editedItemRef.part = null
        }
      }
    },

    destinationSearch() {
      this.categoryOptions.params.filter = this.destinationSearch
      this.pileOptions.params.filter = this.destinationSearch
    },

    search() {
      this.localUnitOptions.params.localUnitName = this.search
      this.projectOptions.params.filter = this.search
    },

    localUnitValue() {
      this.search = this.getSelectedValue()
    },

    projectValue() {
      this.search = this.getSelectedValue()
    }
  }
}
</script>

<style>
.pile-info {
  color: #005792;
}

/* the smart table is below the pile's data: reduce its default full-page height */
.pile-movements-table .smart-table .v-data-table__wrapper {
  height: 50vh;
}

.unload-row {
  background-color: #d1ffd6 !important;
}

.load-row {
  background-color: #fbeeb9 !important;
}

.el-no-wrap {
  white-space: nowrap;
}

.pile-movements-table .smart-table tbody td {
  vertical-align: middle;
}

.attachment-menu-title {
  padding: 10px 16px;
  white-space: nowrap;
  font-size: 1rem;
}

.attachment-menu .v-list-item__title {
  font-size: 0.95rem !important;
}

.load-deletion-preview th,
.load-deletion-preview td {
  white-space: normal;
  overflow-wrap: anywhere;
}

.load-deletion-preview .deletion-detail-value {
  white-space: pre-wrap;
}

.deletion-movements > .v-data-table__wrapper > table {
  min-width: 720px;
}
</style>
